import styles from "./CompleteProfileDialog.module.scss";
import { useContext, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { AuthContext } from "../../context/AuthContext";
import { useAlert } from "../../context/AlertContext";
import { fetcher } from "../../utils/fetcher";
import { WarningIcon } from "../Icons/Warning";

interface CompleteProfileDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

export const CompleteProfileDialog = ({
  open,
  onClose,
  onComplete,
}: CompleteProfileDialogProps) => {
  const authContext = useContext(AuthContext);
  const { setAlert } = useAlert();

  if (!authContext) {
    throw new Error("AuthContext must be used within an AuthProvider");
  }

  const { currentUser, fetchVolunteer } = authContext;

  const [dateOfBirth, setDateOfBirth] = useState<string>(
    (currentUser?.userData as any)?.date_of_birth || ""
  );
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!dateOfBirth) {
      setAlert(
        true,
        "warning",
        "Prosím vyplňte datum narození."
      );
      return;
    }

    if (!currentUser?.userId) {
      setAlert(true, "error", "Uživatel není přihlášen.");
      return;
    }

    setLoading(true);

    try {
      const body = {
        date_of_birth: dateOfBirth,
        step_of_registration: 4,
      };

      await fetcher({
        url: `/volunteer/${currentUser.userId}`,
        method: "PUT",
        body: JSON.stringify(body),
      });

      await fetchVolunteer(currentUser.userId);

      setAlert(
        true,
        "success",
        "Datum narození bylo úspěšně doplněno. Nyní se můžete přihlásit na akce."
      );

      if (onComplete) {
        onComplete();
      }

      onClose();
    } catch (error) {
      console.error("Error updating volunteer profile:", error);
      setAlert(
        true,
        "error",
        "Došlo k chybě při ukládání údajů. Zkuste to prosím znovu."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="complete-profile-dialog-title"
      aria-describedby="complete-profile-dialog-description"
      maxWidth="sm"
      fullWidth
      className={styles.dialog}
    >
      <DialogTitle
        id="complete-profile-dialog-title"
        className={styles.dialogTitle}
      >
        <WarningIcon />
        Doplňte povinné údaje
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <DialogContentText
          id="complete-profile-dialog-description"
          className={styles.description}
        >
          Pro možnost přihlásit se na akci potřebujeme od Vás ještě doplnit
          datum narození. Tyto informace slouží k předvyplnění dokumentu pro
          účast na eventu.
        </DialogContentText>
        <div className={styles.form}>
          <TextField
            id="date_of_birth"
            type="date"
            label="Datum narození"
            variant="outlined"
            size="small"
            fullWidth
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            required
          />
        </div>
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          Zrušit
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !dateOfBirth}
        >
          {loading ? "Ukládám..." : "Uložit a pokračovat"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

